import { create } from 'zustand';
import { createJSONStorage, persist, StateStorage, subscribeWithSelector } from 'zustand/middleware';
import { Account } from 'model/IChat';
import AccountRoles from 'utils/AccountRoles';
import { isCordova } from 'utils/platform';
import { fetchAccountInformation } from 'modules/authentication/helpers/nextauth';

const storage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    if (isCordova()) {
      try {
        return await new Promise((resolve, reject) => {
          try {
            NativeStorage.getItem(name, value => resolve(JSON.parse(value)), reject);
          } catch (error) {
            console.log('failed to get from native storage [inner]', name, error);
            reject(error);
          }
        });
      } catch (error) {
        console.log('failed to get from native storage', name, error);
        return null;
      }
    }
    return null;
  },
  setItem: async (name: string, value: string): Promise<void> => {
    if (isCordova()) {
      try {
        return await new Promise((resolve, reject) => {
          try {
            NativeStorage.setItem(name, JSON.stringify(value), resolve, reject);
          } catch (error) {
            console.log('failed to set to native storage [inner]', name, value, error);
            reject(error);
          }
        });
      } catch (error) {
        console.log('failed to set to native storage', name, value, error);
        return null;
      }
    }
    return null;
  },
  removeItem: async (name: string): Promise<void> => {
    if (isCordova()) {
      try {
        return await new Promise((resolve, reject) => {
          try {
            NativeStorage.remove(name, resolve, reject);
          } catch (error) {
            console.log('failed to remove from native storage [inner]', name, error);
            reject(error);
          }
        });
      } catch (error) {
        console.log('failed to remove from native storage', name, error);
        return null;
      }
    }
    return null;
  }
};

export type AuthStateFields = {
  token: string;
  user: any;
  spy: any;
  loggedInAs: any;
  loading: boolean;
  account: Account | null;
  realAccount: Account | null;
}

export type AuthStateFunctions = {
  _hasHydrated: boolean;
  setHasHydrated: (hasHydrated: boolean) => void;
  getRoles: () => AccountRoles;
  setUser: (user: any) => void;
  setSpy: (spy: any) => void;
  setAccount: (account: Account | null) => void;
  setRealAccount: (account: Account | null) => void;
  setToken: (token: string) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
  isLoggedIn: () => boolean;
  isAdmin: () => boolean;
  isHost: () => boolean;
}

const initialState: AuthStateFields = {
  token: null,
  user: null,
  spy: null,
  account: null,
  realAccount: null,
  loggedInAs: null,
  loading: false
};

type AuthState = AuthStateFunctions & AuthStateFields;

const useAuthStore = create<AuthState>()(
  subscribeWithSelector(
    persist(
      (set, get): AuthState => ({
        ...initialState,
        _hasHydrated: false,
        setHasHydrated: (hasHydrated: boolean) => set({ _hasHydrated: hasHydrated }),
        isAdmin: () => get().isLoggedIn() && (get().user?.roles?.includes('admin') || get().spy?.roles?.includes('admin')),
        isHost: () => get().isLoggedIn() && get().user?.roles?.includes('host'),
        getRoles: () => new AccountRoles(get().account),
        logout: () => set({ ...initialState }),
        setLoading: (loading: boolean) => set({ loading }),
        setAccount: (account: Account) => set({ account }),
        setRealAccount: (account: Account | null) => set({ realAccount: account }),
        setUser: (user: any) => set({ user }),
        setSpy: (spy: any) => set({ spy }),
        setToken: (token: string) => set({ token }),
        isLoggedIn: () => !!get().user
      }),
      {
        name: 'auth-storage',
        storage: createJSONStorage(() => storage),
        partialize: (state) => ({ token: state.token }),
        skipHydration: isCordova(),
        onRehydrateStorage: () => {
          return (state, error) => {
            state.setHasHydrated(true);
            if (error) {
              console.log('hydrating failed', error, state);
            } else {
              console.log('hydrating', state);
            }
            fetchAccountInformation(state);
          };
        }
      }
    )
  )
);

export const useUser = () => useAuthStore((state) => ({
  loading: state.loading,
  isLoggedIn: state.isLoggedIn(),
  isHost: state.isHost(),
  isAdmin: state.isAdmin(),
  user: state.user
}));

export const useAccount = () => useAuthStore(state => ({
  loading: state.loading,
  account: state.account,
  realAccount: state.realAccount
}));

// @ts-ignore
window.authStore = useAuthStore;

export default useAuthStore;